import { tokens, mixins } from 'src/design-system';

/**
 * NOTE ON `gap`: jss-default-unit@8.0.2 has no entry for `gap`, so a numeric
 * value is emitted unitless and dropped by the browser. Every gap here is a
 * string with explicit units. For the same reason this file does not use the
 * gap argument of mixins.layout.flexRow/flexColumn.
 */
const styles = () => ({
  summaryBar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
    gap: `${tokens.spacing.sm}px ${tokens.spacing.md}px`,
    backgroundColor: tokens.colors.bgWhite,
    border: `1px solid ${tokens.colors.border}`,
    // Accent echoes the selected-card treatment in the list.
    borderLeft: `3px solid ${tokens.colors.primaryDark}`,
    borderRadius: tokens.radius.md,
    // Extra left padding reserves room for the overlaid expand handle.
    padding: `${tokens.spacing.sm}px ${tokens.spacing.md}px ${tokens.spacing.sm}px ${tokens.spacing.xl}px`,
    marginBottom: tokens.spacing.sm,
  },
  identity: {
    display: 'flex',
    alignItems: 'center',
    gap: `${tokens.spacing.ms}px`,
    minWidth: 0,
  },
  referenceNumber: {
    color: tokens.colors.primaryDark,
    fontSize: tokens.fontSize.h3,
    fontWeight: tokens.fontWeight.bold,
    lineHeight: tokens.lineHeight.tight,
    ...mixins.typography.truncate,
  },
  divider: {
    width: 1,
    height: tokens.spacing.lg,
    backgroundColor: tokens.colors.border,
    flexShrink: 0,
  },
  meta: {
    display: 'flex',
    alignItems: 'center',
    gap: `${tokens.spacing.xs}px`,
    color: tokens.colors.textSecondary,
    fontSize: tokens.fontSize.small,
    whiteSpace: 'nowrap' as const,
  },
  mode: {
    display: 'flex',
    alignItems: 'center',
    gap: `${tokens.spacing.xs}px`,
    color: tokens.colors.text,
    fontWeight: tokens.fontWeight.semibold,
  },
  modeIcon: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },
  separatorDot: {
    backgroundColor: tokens.colors.textTertiary,
    width: tokens.spacing.xxs,
    height: tokens.spacing.xxs,
    borderRadius: tokens.radius.pill,
    flexShrink: 0,
  },
  spotBadge: {
    ...mixins.layout.flexCenter,
    marginLeft: tokens.spacing.xs,
    padding: `0px ${tokens.spacing.xs}px`,
    height: tokens.spacing.md,
    borderRadius: tokens.radius.sm,
    backgroundColor: tokens.colors.error,
    color: tokens.colors.bgWhite,
    fontSize: tokens.fontSize.caption,
    fontWeight: tokens.fontWeight.bold,
    textTransform: 'uppercase' as const,
  },
  route: {
    display: 'flex',
    // Aligned to the top so the arrow sits on the port-code line, not between rows.
    alignItems: 'flex-start',
    gap: `${tokens.spacing.sm}px`,
    minWidth: 0,
  },
  leg: {
    display: 'flex',
    flexDirection: 'column' as const,
    minWidth: 0,
    maxWidth: 120,
  },
  legCode: {
    fontSize: tokens.fontSize.small,
    fontWeight: tokens.fontWeight.bold,
    lineHeight: tokens.lineHeight.tight,
    color: tokens.colors.text,
    ...mixins.typography.truncate,
  },
  legName: {
    marginTop: tokens.spacing.xxs,
    fontSize: tokens.fontSize.caption,
    lineHeight: tokens.lineHeight.tight,
    color: tokens.colors.textTertiary,
    ...mixins.typography.truncate,
  },
  routeArrow: {
    marginTop: tokens.spacing.xxs,
    color: tokens.colors.textTertiary,
    fontSize: tokens.fontSize.small,
    flexShrink: 0,
  },
  containerChip: {
    display: 'flex',
    alignItems: 'center',
    gap: `${tokens.spacing.xs}px`,
    padding: `${tokens.spacing.xs}px ${tokens.spacing.ms}px`,
    borderRadius: tokens.radius.pill,
    border: `1px solid ${tokens.colors.border}`,
    backgroundColor: tokens.colors.bgWhite,
    color: tokens.colors.primaryDark,
    // Buttons do not inherit the page font.
    fontFamily: 'inherit',
    fontSize: tokens.fontSize.small,
    fontWeight: tokens.fontWeight.semibold,
    lineHeight: tokens.lineHeight.normal,
    whiteSpace: 'nowrap' as const,
    cursor: 'pointer',
    flexShrink: 0,
    '&:hover': {
      backgroundColor: tokens.colors.primaryLight,
      borderColor: tokens.colors.primary,
    },
    ...mixins.interactive.focusRing,
  },
  containerMenu: {
    '& .ant-dropdown-menu': {
      maxHeight: 180,
      overflowY: 'auto',
    },
  },
  containerMenuHeader: {
    fontWeight: tokens.fontWeight.semibold,
    fontSize: tokens.fontSize.caption,
    color: tokens.colors.textSecondary,
    backgroundColor: tokens.colors.bgSubtle,
  },
  containerMenuItem: {
    fontSize: tokens.fontSize.small,
    color: tokens.colors.textSecondary,
  },
  apiStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: `${tokens.spacing.xs}px`,
    maxWidth: 220,
    padding: `${tokens.spacing.xs}px ${tokens.spacing.ms}px`,
    borderRadius: tokens.radius.pill,
    backgroundColor: tokens.colors.error,
    color: tokens.colors.bgWhite,
    fontSize: tokens.fontSize.caption,
    fontWeight: tokens.fontWeight.semibold,
    flexShrink: 0,
  },
  apiStatusSuccess: {
    backgroundColor: tokens.colors.success,
  },
  apiStatusMessage: {
    ...mixins.typography.truncate,
  },
  status: {
    display: 'flex',
    alignItems: 'center',
    gap: `${tokens.spacing.sm}px`,
    // Pins the status cluster right without stranding the left content mid-bar.
    marginLeft: 'auto',
    flexShrink: 0,
  },
  carrierLogo: {
    maxWidth: 80,
    maxHeight: tokens.spacing.lg,
    objectFit: 'contain' as const,
    flexShrink: 0,
  },
  currentMilestone: {
    display: 'flex',
    alignItems: 'center',
    gap: `${tokens.spacing.xs}px`,
    padding: `${tokens.spacing.xs}px ${tokens.spacing.ms}px`,
    borderRadius: tokens.radius.pill,
    backgroundColor: tokens.colors.bgWarningSubtle,
    color: tokens.colors.text,
    fontSize: tokens.fontSize.caption,
    fontWeight: tokens.fontWeight.bold,
    whiteSpace: 'nowrap' as const,
  },
  currentMilestoneOnHold: {
    backgroundColor: tokens.colors.bgOnHold,
  },
  tasks: {
    display: 'flex',
    alignItems: 'center',
    gap: `${tokens.spacing.xs}px`,
    padding: `${tokens.spacing.xs}px ${tokens.spacing.ms}px`,
    borderRadius: tokens.radius.pill,
    backgroundColor: tokens.colors.bgSuccessSubtle,
    color: tokens.colors.text,
    fontSize: tokens.fontSize.small,
    whiteSpace: 'nowrap' as const,
  },
  tasksCompleted: {
    fontWeight: tokens.fontWeight.bold,
  },
});

export default styles;
